import { z } from 'zod';
/**
 * Admin view of per-message cascade verdicts (D-025).
 *
 * Two list endpoints share this shape:
 *   - GET /admin/cascade-traces/l1-passed
 *   - GET /admin/cascade-traces/l2-passed
 *
 * Cursor pagination: pass back `before` from the last item's ts to page.
 */
export const CascadeTraceItem = z.object({
    id: z.string().uuid(),
    rawMessageId: z.string().uuid(),
    ts: z.string().datetime(),
    verdict: z.enum([
        'passed_l1',
        'dropped_l2',
        'passed_l2',
        'dropped_l3',
        'lead',
    ]),
    cosine: z.number().nullable(),
    fastConfidence: z.number().nullable(),
    preciseConfidence: z.number().nullable(),
    textSnippet: z.string(),
    senderUsername: z.string().nullable(),
    /** Group title at the time of decision (resolved via groups table). */
    groupTitle: z.string().nullable(),
    leadId: z.string().uuid().nullable(),
    markedAsNoiseAnchorId: z.string().uuid().nullable(),
});
export const ListCascadeTracesQuery = z.object({
    /**
     * Which slice of the cascade to view.
     *   l1         → everything that survived L1
     *   l2         → only rows where L2 actually ran AND didn't drop
     *   l3-dropped → only verdict='dropped_l3' (L3 否决 / boundary cases)
     */
    stage: z.enum(['l1', 'l2', 'l3-dropped']),
    /** ISO timestamp — return traces with ts < before. Omit for newest page. */
    before: z.string().datetime().optional(),
    /** Default 50, max 200. */
    limit: z.coerce.number().int().min(1).max(200).default(50).optional(),
});
export const ListCascadeTracesResponse = z.object({
    items: z.array(CascadeTraceItem),
    /** ts of the last item — pass back as `before` to load next page. */
    nextBefore: z.string().datetime().nullable(),
});
/** Take this trace's text → create a new noise anchor for the tenant. */
export const MarkTraceAsNoiseRequest = z.object({
    /** Optional override; defaults to trace.textSnippet. */
    text: z.string().optional(),
    note: z.string().optional(),
});
export const MarkTraceAsNoiseResponse = z.object({
    noiseAnchorId: z.string().uuid(),
    text: z.string(),
});
/** Bulk delete all traces matching the current page's stage filter. */
export const ClearCascadeTracesQuery = z.object({
    stage: z.enum(['l1', 'l2', 'l3-dropped']),
});
export const ClearCascadeTracesResponse = z.object({
    deleted: z.number().int(),
});
//# sourceMappingURL=cascade-trace.js.map